import React, { useState, useCallback } from 'react';
import {
  InputGroup,
  InputLeftElement,
  InputRightElement,
  InputProps as ChakraInputProps,
  ComponentWithAs,
} from '@chakra-ui/react';
import { Icon, Layout, ClearInputButton, ShowHidePasswordButton, InputProps } from '../../';

type WithInputAddonsProps = InputProps & {
  icon?: {
    name?: string;
    ariaLabel?: string;
    onClick?: () => void;
  };
  showPassword?: boolean;
  toggleShowPassword?: () => void;
  ref: React.RefObject<HTMLInputElement>;
};

export function withInputAddons(componentName: string) {
  return (WrappedInput: ComponentWithAs<'input', ChakraInputProps>) => {
    const ComponentWithInputAddons = (props: WithInputAddonsProps) => {
      const {
        leftAddOn,
        rightAddOn,
        clearField,
        icon,
        type,
        showPassword,
        ref,
        onChange,
        toggleShowPassword,
        isReadOnly,
        ...rest
      } = props;
      const inputRef = React.useRef<HTMLInputElement>(null);
      const [value, setValue] = useState(rest.value);
      const handleChange = useCallback(
        event => {
          onChange?.(event); // ensure input's original onChange gets called too
          setValue(event.target.value);
        },
        [onChange]
      );
      const handleClearField = useCallback(() => {
        clearField?.();
        setValue('');
      }, [clearField]);
      return (
        <InputGroup>
          {(leftAddOn || type === 'search') && (
            <InputLeftElement marginLeft="4">
              {leftAddOn}
              {type === 'search' && <Icon iconName="search" prefix="fas" size="sm" />}
            </InputLeftElement>
          )}
          <WrappedInput
            type={type}
            ref={inputRef}
            onChange={handleChange}
            {...rest}
            css={!value && type === 'date' && 'padding-inline-end: 4px'}
          />
          <InputRightElement>
            <Layout variant="flex" alignContent="center">
              <Layout>
                {value && value !== '' && !isReadOnly && (
                  <ClearInputButton clearField={handleClearField} inputRef={inputRef} />
                )}
              </Layout>
              {rightAddOn && (
                <Layout alignSelf="center" marginRight="2">
                  {rightAddOn}
                </Layout>
              )}
              {toggleShowPassword && showPassword !== undefined && (
                <ShowHidePasswordButton toggleShowPassword={toggleShowPassword} showPassword={showPassword} />
              )}
            </Layout>
          </InputRightElement>
        </InputGroup>
      );
    };
    ComponentWithInputAddons.displayName = componentName;
    return ComponentWithInputAddons;
  };
}
